import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell, Check, CheckCheck, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  useDeleteNotification,
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
  useNotifications,
  useUnreadNotificationCount,
  useRealtimeNotifications,
} from '../../hooks/queries/useNotifications';
import { useSidecarStore } from '../../store/useSidecarStore';
import { useTaskModalStore } from '../../store/taskModalStore';
import {
  getNotificationTarget,
  NOTIFICATION_TYPE_ICONS as TYPE_ICONS,
} from '../../services/notifications/notificationTargets';
import type { Notification } from '../../services/notifications/notificationService';
import {
  circuitIdFromMetadata,
  nextSignatureToSignAlerts,
} from '../../services/notifications/signatureAlertToast';

export function NotificationBell() {
  const navigate = useNavigate();
  useRealtimeNotifications();
  const { data: notifications = [], isSuccess } = useNotifications(20);
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const seenRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!isSuccess) return;
    const incoming = notifications.map((row) => ({
      id: row.id,
      type: row.type,
      is_read: row.is_read,
      title: row.title,
      message: row.message ?? '',
      metadata: (row.metadata ?? null) as Record<string, unknown> | null,
    }));
    const { alerts, nextSeen } = nextSignatureToSignAlerts(seenRef.current, incoming);
    seenRef.current = nextSeen;
    for (const alert of alerts) {
      const circuitId = circuitIdFromMetadata(alert.metadata);
      toast.info(alert.title, {
        description: alert.message,
        duration: 8_000,
        action: circuitId
          ? { label: 'Ouvrir', onClick: () => navigate(`/parapheur/${circuitId}`) }
          : undefined,
      });
    }
  }, [isSuccess, notifications, navigate]);
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const deleteOne = useDeleteNotification();

  const selectSidecarChannel = useSidecarStore((s) => s.selectChannel);
  const openTask = useTaskModalStore((s) => s.openTask);

  function handleClickNotification(n: Notification) {
    if (!n.is_read) markAsRead.mutate(n.id);
    const target = getNotificationTarget(n);
    if (!target) return;
    if (target.type === 'url') navigate(target.url);
    else if (target.type === 'task') openTask(target.taskId);
    else selectSidecarChannel(target.channelId, target.messageId);
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          className="relative w-9 h-9 flex items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors"
        >
          <Bell className="w-5 h-5" aria-hidden />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#ea7549] text-white text-[10px] font-semibold leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="w-[380px] max-h-[480px] bg-white rounded-cime shadow-lg border border-neutral-100 overflow-hidden flex flex-col z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
            <p className="text-sm font-semibold text-neutral-800">Notifications</p>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead.mutate()}
                className="text-xs text-cime-primary hover:underline flex items-center gap-1"
                title="Tout marquer comme lu"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Marquer tout comme lu
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Bell className="w-8 h-8 text-neutral-300 mb-2" />
                <p className="text-xs text-neutral-400">Aucune notification</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`group flex gap-3 px-4 py-3 border-b border-neutral-50 last:border-b-0 hover:bg-neutral-50 transition-colors cursor-pointer ${
                    !n.is_read ? 'bg-cime-primary/[0.03]' : ''
                  }`}
                  onClick={() => handleClickNotification(n)}
                >
                  <div className="text-lg flex-shrink-0 leading-none pt-0.5">
                    {TYPE_ICONS[n.type] ?? '🔔'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <p className="text-sm font-medium text-neutral-800 flex-1 truncate">
                        {n.title}
                      </p>
                      {!n.is_read && (
                        <span className="w-2 h-2 rounded-full bg-cime-primary flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    <p className="text-xs text-neutral-500 line-clamp-2 mt-0.5">{n.message}</p>
                    <p className="text-[10px] text-neutral-400 mt-1">
                      {n.created_at
                        ? formatDistanceToNow(new Date(n.created_at), {
                            addSuffix: true,
                            locale: fr,
                          })
                        : ''}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!n.is_read && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead.mutate(n.id);
                        }}
                        title="Marquer comme lu"
                        className="p-1 rounded text-neutral-400 hover:text-cime-primary hover:bg-white"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteOne.mutate(n.id);
                      }}
                      title="Supprimer"
                      className="p-1 rounded text-neutral-400 hover:text-red-600 hover:bg-white"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="px-4 py-2 border-t border-neutral-100 bg-neutral-50">
            <button
              onClick={() => navigate('/notifications')}
              className="text-xs text-neutral-500 hover:text-cime-primary w-full text-center"
            >
              Voir toutes les notifications
            </button>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
